import { GAME_VERSION } from '../version';
import mensLeaguesData from './mens/leagues';
import mensTeamsData from './mens/teams';
import womensLeaguesData from './womens/leagues';
import womensTeamsData from './womens/teams';
import { PlayerGenerator } from '../services/playerGenerator';
import { mensGameConfig } from './mens/config';
import { womensGameConfig } from './womens/config';
import { GameConfig } from '../domain/entities';

const createTeam = (name: string, baseSkill: number, balance: number, colors: {primary: string, secondary: string}, gender: 'male' | 'female', leagueName: string, config: GameConfig) => ({
    name,
    baseSkill,
    squad: PlayerGenerator.generateSquad(baseSkill, 22, gender, leagueName, config),
    played: 0, won: 0, drawn: 0, lost: 0, goalsFor: 0, goalsAgainst: 0, points: 0,
    tactics: { 
        formation: '4-4-2', 
        mentality: 'Balanced', 
        lineup: {},
        attackingFocus: 'Mixed',
        passingStyle: 'Mixed',
        defensiveStyle: 'Standard',
        tacklingStyle: 'Normal',
    },
    finances: { balance, history: [{season: 1, week: 0, event: 'Initial Balance', amount: balance}] },
    colors,
    youthAcademy: { level: 1, investment: 0, squad: [] },
    assistantManager: { level: 1, transferSuggestions: [] },
    trainingFocus: 'Fitness',
    positionalTrainingFocus: { GK: null, DEF: null, MID: null, FWD: null },
    trainingFacilities: { level: 1 },
    clubHistory: {
        mostAppearances: { playerName: '', value: 0 },
        mostGoals: { playerName: '', value: 0 },
        recordTransferFee: { playerName: '', fee: 0, season: 0, type: 'in' },
        recordTransferIn: { playerName: '', fee: 0, season: 0 },
        recordTransferOut: { playerName: '', fee: 0, season: 0 },
        playerStats: {},
        retiredLegends: [],
    },
    boardConfidence: 50,
    seasonObjectives: [],
    positionHistory: [],
});

const buildGameData = (leaguesData: any, teamsData: any, config: GameConfig, gameType: 'mens' | 'womens', restrictTeamCount?: number) => {
    const leagues: any = {};
    PlayerGenerator.resetPlayerIdCounter();
    const genderForPlayers: 'male' | 'female' = gameType === 'womens' ? 'female' : 'male';
    for (const leagueName in leaguesData) {
        let leagueTeams = (teamsData[leagueName as keyof typeof teamsData] || []);

        // If restrictTeamCount is set, randomly select that many teams from the available teams
        if (restrictTeamCount && restrictTeamCount > 0 && restrictTeamCount < leagueTeams.length) {
            const shuffled = [...leagueTeams].sort(() => Math.random() - 0.5);
            leagueTeams = shuffled.slice(0, restrictTeamCount);
        }

        leagues[leagueName] = {
            ...leaguesData[leagueName as keyof typeof leaguesData],
            teams: leagueTeams.map((team: any) => createTeam(team.name, team.baseSkill, team.balance, team.colors, genderForPlayers, leagueName, config))
        };
    }
    return {
        version: GAME_VERSION,
        gameType,
        leagues,
        config,
    };
};

export const defaultGameData = buildGameData(mensLeaguesData, mensTeamsData, mensGameConfig, 'mens');
export const womenGameData = buildGameData(womensLeaguesData, womensTeamsData, womensGameConfig, 'womens');

export const buildRestrictedGameData = (gameType: 'mens' | 'womens', teamCount: number) => {
    if (gameType === 'womens') {
        return buildGameData(womensLeaguesData, womensTeamsData, womensGameConfig, 'womens', teamCount);
    } else {
        return buildGameData(mensLeaguesData, mensTeamsData, mensGameConfig, 'mens', teamCount);
    }
};